import React, { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, Typography, Textarea } from "@mui/joy";
import { FileText } from "lucide-react";

import { PROJECT_NOTES_MAX_LENGTH } from "./estimatingDefaults";

interface ProjectNotesProps {
    value: string;
    onSave: (value: string) => Promise<void>;
}

export default function ProjectNotes({ value, onSave }: ProjectNotesProps) {
    const [draft, setDraft] = useState(value);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setDraft(value);
    }, [value]);

    const atLimit = draft.length >= PROJECT_NOTES_MAX_LENGTH;
    const isDirty = draft !== value;

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave(draft);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Card variant="soft" sx={{ border: "1px solid", borderColor: "divider" }}>
            <CardContent>
                <Typography level="title-sm" mb={1} startDecorator={<FileText size={18} />}>
                    Project Notes
                </Typography>
                <Typography level="body-xs" sx={{ mb: 1.5 }}>
                    Special instructions for this job. Appears on the contract under Special Project Notes.
                </Typography>
                <Textarea
                    minRows={4}
                    placeholder="Access details, customer requests, scope exceptions..."
                    value={draft}
                    onChange={(e) => setDraft(e.target.value.slice(0, PROJECT_NOTES_MAX_LENGTH))}
                    slotProps={{
                        textarea: {
                            maxLength: PROJECT_NOTES_MAX_LENGTH,
                        },
                    }}
                    sx={{ width: "100%" }}
                />
                <Box
                    sx={{
                        mt: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                    }}
                >
                    <Typography level="body-xs" color={atLimit ? "warning" : "neutral"}>
                        {draft.length}/{PROJECT_NOTES_MAX_LENGTH}
                        {isDirty ? " · unsaved changes" : ""}
                    </Typography>
                    <Button
                        size="sm"
                        variant="solid"
                        loading={isSaving}
                        disabled={!isDirty || isSaving}
                        onClick={handleSave}
                    >
                        Save notes
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
}
